import type { NodeApi } from "@hogartrabe/react-arborist-vrh";
import type { Project } from "../projectStore";
import type { Node } from "interfaces/schemas";
import {
  calculatePartiallyVisible,
  popNodeFromParent,
  updateAncestorsActiveStatus,
  updateSiblingsAfterPop,
} from "./utils";
import type { NodeRequestItem } from "./utils/createNodeInput";
import * as treeUtils from "utils/treeUtils";
import { deleteWizard, processNodes } from "./utils/mutations";

export const deleteNode = (
  node: NodeApi,
  tree: Node[],
  project: Project,
  language: string,
  callback?: (node: Node) => void,
) => {
  if (!node.parent) {
    throw new Error("Cannot delete root node");
  }
  if (!project) {
    throw new Error("Project data is required to delete a node");
  }
  const nodesToUpdate = new Map();
  const popResult = popNodeFromParent(node.parent.data, node.data.id);
  if (!popResult) {
    throw new Error("Node to delete not found in parent");
  }

  const { node: removedNode, index: removedIndex } = popResult;

  updateSiblingsAfterPop(node.parent?.data.nodes, removedIndex, nodesToUpdate);

  const oldParentPartiallyVisible = calculatePartiallyVisible(node.parent.data);

  const oldAncestorsToUpdate: NodeRequestItem[] = [];

  updateAncestorsActiveStatus(node.parent, oldAncestorsToUpdate, project.id, {
    prev: {
      active: treeUtils.isLeafNode(node.parent.data) && node.parent.data.active,
      partiallyVisible: oldParentPartiallyVisible,
    },
  });

  const isWizardRoot = treeUtils.isWizardRootFolder(node.data);

  const updateNodes = Array.from(nodesToUpdate.values());

  const {
    nodes: _nodes,
    partiallyVisible: _partiallyVisible,
    ...nodeToDelete
  } = removedNode;

  processNodes(
    [
      {
        action: "DELETE",
        nodes: isWizardRoot ? [] : [nodeToDelete],
      },
      {
        action: "UPDATE",
        nodes: [...oldAncestorsToUpdate, ...updateNodes],
      },
    ],
    project,
    language,
  );

  if (isWizardRoot) {
    deleteWizard(removedNode.id, project, language);
  }
  callback?.(removedNode);

  return [...tree];
};
